import type { Media } from 'types';
import type { WrapProps } from '@leafwell/components';
import type { PageProps } from 'lib/queries/pages';
import type { InPersonEvents } from 'types/in-person-events';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';
import camelcaseKeys from 'camelcase-keys';
import { usePostHog } from 'posthog-js/react';
import { Wrap } from '@leafwell/components';
import HeadingBody from 'components/heading-body';
import { useWordPressContext } from 'contexts/wordpress-provider';
import { request } from 'lib/rest';
import { useCurrentState } from 'hooks/useCurrentState';

import SignupSingleButton from './signup-single-button';
import InPersonEventModal from './in-person-event-modal';
import SignupFormSimple from './signup-form-simple';
import { isSupportedState } from 'lib/data/do-i-qualify-states';
import DoIQualify from './do-i-qualify';
import SignupFormPasswordless from './signup-form-passwordless';
import PriceTag, { PriceTagProps } from './price-tag';

export type HeroCardProps = Partial<Pick<WrapProps, 'className' | 'spacer'>> &
  Pick<
    PageProps['page']['acf'],
    | 'hero'
    | 'states'
    | 'affiliateLogo'
    | 'pauseAppointments'
    | 'redirectMedicalCard'
  > & {
    redirectOnStateChange?: boolean;
    redirectStatePage?: boolean;
    inPersonEvents?: InPersonEvents;
    useDarkBackground?: boolean;
    addEmailInput?: boolean;
    showDoIQualify?: boolean;
    passwordless?: boolean;
    priceTag?: PriceTagProps | null;
  };

type PartnerProps = {
  title: string;
  logo: Media;
  website: string;
  couponText?: string;
};

const PartnerData: React.FC<PartnerProps> = ({
  couponText,
  title,
  logo,
  website,
}) => {
  return (
    <div className="relative flex items-center gap-x-3 rounded-t-[7px] bg-white px-5 py-3 border-b border-green-500">
      <div className="flex flex-col justify-center">
        {!couponText ? (
          <p className="text text-pretty text-lg">
            Leafwell is proud to partner <br />
            with <strong>{title}</strong>
          </p>
        ) : null}
        {couponText ? (
          <div
            className="inline-block"
            dangerouslySetInnerHTML={{
              __html: couponText,
            }}
          />
        ) : null}
      </div>
      {logo ? (
        <div className="w-32 flex items-center justify-center">
          <Image src={logo.src} alt={logo.alt || ''} width={100} height={100} />
        </div>
      ) : null}
    </div>
  );
};

const HeroCardForm: React.FC<HeroCardProps> = ({
  hero,
  states,
  inPersonEvents,
  useDarkBackground = false,
  addEmailInput = false,
  showDoIQualify = false,
  passwordless = false,
  priceTag = null,
}) => {
  const posthog = usePostHog();
  const router = useRouter();
  const { query, asPath } = router;
  const [partnerData, setPartnerData] = useState<PartnerProps | null>(null);
  const [isReferred, setIsReferred] = useState<boolean>(false);
  const [referrerName, setReferrerName] = useState<string | null>(null);

  const currentState = useCurrentState(states);

  const quickLinks = hero.quickLinks;

  useEffect(() => {
    if (query.utm_source && !partnerData) {
      // call api to fetch partner data based on `utm_source`
      request(`/partner?slug=${query.utm_source}`).then(res => {
        if (res.data && res.data?.totalItems > 0) {
          const { title, acf } = camelcaseKeys(res.data.items[0], {
            deep: true,
          });
          const { logo, website, couponText, states } = acf;

          if (
            !states.length ||
            (states.length && states.includes(currentState))
          ) {
            setPartnerData({
              title,
              logo,
              website,
              couponText,
            });
          }
        }
      });
    }
  }, [query]);

  useEffect(() => {
    if (query.ref_id) {
      setIsReferred(true);
      posthog.identify(posthog.get_distinct_id(), {
        ref_id: query.ref_id as string,
        referral_type: query?.discount_code ? 'email' : 'sms',
      });
    }
    if (query.name) {
      setReferrerName(query.name as string);
    }
  }, [query.ref_id, query.name]);

  useEffect(() => {
    if (query.ref_id && !query.discount_code) {
      fetch('/api/get-discount-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: 15 }),
      })
        .then(res => res.json())
        .then(({ code }) => {
          if (!code) return;
          router.replace(
            {
              pathname: router.pathname,
              query: { ...router.query, discount_code: code },
            },
            undefined,
            { shallow: true },
          );
        })
        .catch(err => console.error('[WOM] get-discount-code failed:', err));
    }
  }, [query.ref_id, query.discount_code]);

  useEffect(() => {
    if (query.knowledgebase_id) {
      const wuithoutSearchParams = asPath.split('?')[0];
      const newUrl = `${wuithoutSearchParams}#${query.knowledgebase_id}`;
      router.push(newUrl);
    }
  }, [query]);

  return (
    <>
      <section
        className={`relative grid ${
          useDarkBackground ? 'bg-[#3f1a7c]' : 'bg-lilac-50'
        } xl:-mt-10 xl:pt-10 lw-state-pages`}
      >
        <div className="hidden lg:block absolute inset-y-0 right-0 w-[70%] 3xl:w-[60%] pointer-events-none">
          {hero.image ? (
            <Image
              src={hero.image.src}
              alt={hero.image.alt || ''}
              fill
              className="object-cover object-top"
              priority
            />
          ) : (
            <Image
              src="/remove-signup-form-exp.png"
              alt=""
              fill
              className="object-cover object-top"
              priority
            />
          )}

          <div
            className="absolute inset-0"
            style={{
              background: useDarkBackground
                ? 'linear-gradient(100deg, #3f1a7c 10%, #3f1a7ccc 20%, transparent 65%)'
                : 'linear-gradient(100deg, #f5f1fd 10%, #f5f1fdcc 20%, transparent 65%)',
            }}
          />
        </div>
        {isReferred ? (
          <div className="mx-4 lg:mx-auto py-2 md:py-4 max-w-screen-2xl bg-green-50 border border-green-200 rounded-xl mt-4 md:mt-0 z-10">
            <div className="flex items-center justify-start px-4 gap-x-6">
              <p className="font-semibold uppercase text-dark-green rounded-full px-2 py-1 bg-lilac-50 border border-green-200 text-xs shadow-sm">
                Referred
              </p>
              <div>
                <p className="font-semibold leading-tight text-base lg:text-lg">
                  {referrerName
                    ? `${referrerName} shared this with you! Sign up to get your discount.`
                    : 'A friend shared this with you! Sign up to get your discount.'}
                </p>
              </div>
            </div>
          </div>
        ) : null}
        <Wrap className="items-start z-10 relative" id="leafwell-signup-form">
          <div
            className={`flex flex-col gap-y-4 lg:gap-y-8 text-pretty lg:max-w-2xl ${
              partnerData ? '' : 'lg:pb-10'
            }`}
          >
            <HeadingBody
              level="1"
              title={hero.title}
              className={`font-semibold ${
                useDarkBackground ? 'text-white' : 'text-black'
              }`}
            />
            <div className="block relative h-44 lg:hidden w-full rounded-2xl overflow-hidden">
              {hero.image ? (
                <Image
                  src={hero.image.src}
                  alt={hero.image.alt || ''}
                  width={600}
                  height={400}
                  className="w-full object-cover"
                  priority
                />
              ) : (
                <Image
                  src="/remove-signup-form-exp.png"
                  alt=""
                  width={600}
                  height={400}
                  className="w-full object-cover"
                  priority
                />
              )}

              <div className="inline-flex absolute bottom-2 right-2 z-10 items-center gap-x-2 bg-[#F5F1FDE5] opacity-90 rounded-lg px-3 py-1 shadow-sm text-sm font-medium">
                <span className="text-green-500 text-lg">★</span>
                Trusted By{' '}
                <span className="text-purple-500 font-semibold">
                  1 Million
                </span>{' '}
                Monthly Users
              </div>
            </div>
            {hero.description?.length > 0 ? (
              <div
                className={`!text-lg lg:!text-2xl ${
                  useDarkBackground ? 'text-white' : 'text-black'
                }`}
                dangerouslySetInnerHTML={{ __html: hero.description }}
              />
            ) : null}
            {inPersonEvents?.locations?.length > 0 && partnerData ? (
              <InPersonEventModal
                inPersonEvents={inPersonEvents}
                currentState={currentState}
                formSubmitText={hero.formSubmitText}
                className="w-full lg:w-72"
              />
            ) : null}
            {showDoIQualify && isSupportedState(currentState) && partnerData ? (
              <DoIQualify
                currentState={currentState}
                className="w-full lg:w-72"
                passwordless
              />
            ) : null}
            {partnerData ? (
              <>
                <div id="hero-cta-sentinel">
                  <SignupFormPasswordless
                    stateCode={currentState}
                    formSubmitText={hero.formSubmitText || 'Get your card'}
                    ctaLocation="hero-state-page"
                    partnerData={
                      partnerData ? <PartnerData {...partnerData} /> : undefined
                    }
                  />
                </div>
              </>
            ) : (
              <div
                className={`flex flex-col ${
                  addEmailInput || passwordless ? '' : 'lg:flex-row'
                } gap-y-4 lg:gap-x-4`}
              >
                {inPersonEvents?.locations?.length > 0 ? (
                  <InPersonEventModal
                    inPersonEvents={inPersonEvents}
                    currentState={currentState}
                    formSubmitText={hero.formSubmitText}
                    className={`w-full lg:w-72 min-h-50 ${
                      passwordless ? '' : 'order-first lg:order-last'
                    }`}
                  />
                ) : null}
                <div id="hero-cta-sentinel">
                  <SignupFormPasswordless
                    stateCode={currentState}
                    formSubmitText={hero.formSubmitText || 'Get your card'}
                    ctaLocation="hero-state-page"
                    priceTag={priceTag ? <PriceTag {...priceTag} /> : undefined}
                  />
                </div>
                {showDoIQualify && isSupportedState(states[0]) ? (
                  <DoIQualify
                    currentState={currentState}
                    className="w-full lg:w-72"
                    passwordless
                  />
                ) : null}
              </div>
            )}
          </div>
          <div className="hidden lg:flex absolute bottom-6 right-6 z-10 items-center gap-x-2 bg-[#F5F1FDE5] opacity-90 rounded-lg px-3 py-1 shadow-sm text-sm font-medium">
            <span className="text-green-500 text-lg">★</span>
            Trusted By{' '}
            <span className="text-purple-500 font-semibold">
              1 Million
            </span>{' '}
            Monthly Users
          </div>
        </Wrap>
      </section>
      {quickLinks?.length > 0 && (
        <section className="bg-[#F9F9F9]">
          <ul className="w-full px-4 lg:px-10 mx-auto py-6 max-w-screen-2xl flex flex-col md:flex-row gap-3 md:gap-6 divide-y md:divide-y-0 md:divide-x divide-[#C3CDD5]">
            {quickLinks.map((link, index) => {
              const isFirst = index === 0;
              return (
                <li
                  key={link.title}
                  className={`flex-1 pb-2 ${
                    isFirst ? 'md:pl-0' : 'md:pl-8 pt-8 md:pt-0'
                  }`}
                >
                  <a
                    href={`#${link.section}`}
                    className="flex flex-col gap-y-2 max-w-xl"
                  >
                    <div className="flex items-center lg:justify-center gap-x-4">
                      {link.icon ? (
                        <Image
                          src={link.icon.src}
                          alt={link.icon.alt || ''}
                          width={32}
                          height={32}
                          className="w-8 h-8"
                        />
                      ) : null}
                      <p className="text-base font-semibold">{link.title}</p>
                    </div>
                  </a>
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </>
  );
};

const HeroCardNew: React.FC<HeroCardProps> = props => {
  const { disableMedicalCard } = useWordPressContext();
  const {
    redirectStatePage,
    affiliateLogo,
    pauseAppointments,
    redirectMedicalCard,
  } = props;

  let redirectOnStateChange = true;

  if (redirectStatePage) {
    redirectOnStateChange = redirectStatePage;
  }

  if (disableMedicalCard) {
    redirectOnStateChange = false;
  }

  // this is an affiliate page
  if (affiliateLogo) {
    redirectOnStateChange = false;
  }

  // this page isn't active on medical card and a hubspot form is displayed
  if (pauseAppointments || !redirectMedicalCard) {
    redirectOnStateChange = false;
  }

  return (
    <HeroCardForm redirectOnStateChange={redirectOnStateChange} {...props} />
  );
};

export default HeroCardNew;
